/**
 * The Track Changes "left off" note (spec §5.2). To apply a tracked edit in a document that does not track
 * changes, Ella switches Track Changes on and puts the user's mode back afterwards. The mode it must go back to
 * is written here first and removed once it has been put back, so if the pane is closed, reloaded or crashes in
 * between (or the restore fails), the next pane load finds the note and offers a one-click restore.
 *
 * Kept in the pane's sessionStorage (never in the document, never on the server). Storage can be missing or
 * throw (private mode, blocked site data), so every access is guarded and the pane works without it.
 */

import type { TrackingMode } from "../hands/types";

export const FLIP_KEY = "ella.trackingFlip";
/** An older note is ignored (and removed): nobody expects a restore offer about yesterday. */
export const FLIP_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface FlipRecord {
  docKey: string;
  /** Whether that doc_key is saved in the document (else it only lived in the pane that wrote the note). */
  persisted: boolean;
  previousMode: TrackingMode;
  startedAt: number;
}

export type SessionStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const MODES: ReadonlySet<string> = new Set(["Off", "TrackAll", "TrackMineOnly"]);

export function defaultSessionStore(): SessionStore | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function writeFlip(store: SessionStore | null, record: FlipRecord): void {
  try {
    store?.setItem(FLIP_KEY, JSON.stringify(record));
  } catch {
    // No storage: the in-session restore still runs; only the after-a-crash offer is lost.
  }
}

export function clearFlip(store: SessionStore | null): void {
  try {
    store?.removeItem(FLIP_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function readFlip(store: SessionStore | null): FlipRecord | null {
  let raw: string | null;
  try {
    raw = store?.getItem(FLIP_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<FlipRecord>;
    if (
      typeof value.docKey !== "string" ||
      typeof value.previousMode !== "string" ||
      !MODES.has(value.previousMode) ||
      typeof value.startedAt !== "number" ||
      !Number.isFinite(value.startedAt)
    ) {
      return null;
    }
    return { docKey: value.docKey, persisted: value.persisted === true, previousMode: value.previousMode as TrackingMode, startedAt: value.startedAt };
  } catch {
    return null;
  }
}

/** How a mode reads in a sentence. */
export function modeLabel(mode: TrackingMode): string {
  if (mode === "TrackAll") return "on";
  if (mode === "TrackMineOnly") return "on for your changes only";
  return "off";
}
